<template>
    <div class="db-card h-full flex flex-col">
        <!-- Card Header -->
        <div class="db-card-header flex items-center justify-between">
            <div class="flex items-center gap-2.5">
                <span class="relative flex h-2.5 w-2.5">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <h3 class="font-medium text-lg text-heading m-0 flex items-center gap-2">
                    {{ $t('label.live_activity') }}
                </h3>
                <span v-if="events.length > 0" class="inline-block leading-none px-2 py-1.5 pt-2 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                    {{ filteredEvents.length }}
                </span>
            </div>

            <!-- Header controls -->
            <div class="flex items-center gap-2 mt-1 mb-1">
                <!-- Filtro rápido / toggle no leídos -->
                <button
                    type="button"
                    @click="filterOnlyUnread = !filterOnlyUnread"
                    :class="filterOnlyUnread ? 'bg-primary/10 text-primary border-primary/30' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'"
                    class="h-7 px-2 text-xs font-medium rounded-lg border transition-all flex items-center gap-1.5 shadow-sm"
                    :title="$t('label.filter_unread_events')">
                    <i class="fa-regular fa-bell text-[11px]"></i>
                    <span class="hidden sm:inline">{{ filterOnlyUnread ? $t('label.all_events') : $t('label.unread_only') }}</span>
                </button>

                <!-- Botón de Engrane para Configuración de Filtros -->
                <button
                    type="button"
                    @click="toggleConfigModal"
                    class="w-7 h-7 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 flex items-center justify-center transition shadow-sm"
                    :title="$t('label.configure_visible_events')">
                    <i class="fa-solid fa-gear text-xs"></i>
                </button>

                <!-- Refresh manual -->
                <button
                    type="button"
                    @click="refreshManual"
                    :disabled="isFetching"
                    class="w-7 h-7 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-500 flex items-center justify-center transition disabled:opacity-50 shadow-sm"
                    :title="$t('label.refresh_feed')">
                    <i class="fa-solid fa-arrows-rotate text-xs" :class="{ 'animate-spin': isFetching }"></i>
                </button>
            </div>
        </div>

        <!-- Card Body / Feed List -->
        <div class="db-card-body p-4 flex-1 flex flex-col min-h-[200px]">
            <!-- Badge indicador de cola entrante (solo para nuevos eventos en vivo, no en carga inicial) -->
            <div v-if="isProcessingQueue && !isInitialStaggering" class="mb-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200/60 rounded-lg text-emerald-700 text-xs flex items-center justify-between animate-pulse">
                <span class="flex items-center gap-2 font-medium">
                    <i class="fa-solid fa-bolt text-emerald-500"></i>
                    {{ $t('label.incoming_events') }}
                </span>
                <span class="text-[11px] font-semibold bg-emerald-200/60 px-1.5 py-0.5 rounded">
                    +{{ pendingQueue.length }}
                </span>
            </div>

            <!-- Listado con TransitionGroup -->
            <div
                ref="feedScrollContainer"
                @scroll="handleContainerScroll"
                class="flex-1 overflow-y-auto max-h-[240px] thin-scrolling"
                :class="{ 'is-scrolling': isFeedScrolling }"
            >
                <TransitionGroup name="stagger-slide" tag="div" class="flex flex-col gap-2.5">
                    <div
                        v-for="item in filteredEvents"
                        :key="item.id"
                        @click="handleEventClick(item)"
                        class="activity-row relative flex items-center justify-between p-3 rounded-lg border border-slate-200/80 bg-white transition-all duration-200 cursor-pointer overflow-hidden"
                        :class="getSeverityBorder(item)"
                    >
                        <!-- Indicador visual sutil de fondo si no está leído -->
                        <div v-if="!item.read_at" class="absolute inset-0 bg-primary/[0.02] pointer-events-none"></div>

                        <!-- Left: Avatar / Badge + Content -->
                        <div class="flex items-center gap-3 min-w-0 pr-3 z-10">
                            <!-- Badge redondo con icono correspondiente -->
                            <div
                                class="row-avatar w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold transition-transform duration-200"
                                :class="getBadgeStyles(item)"
                            >
                                <i :class="item.icon || 'fa-solid fa-circle-info'" class="text-xs"></i>
                            </div>

                            <!-- Text Content -->
                            <div class="min-w-0 flex-1">
                                <div class="flex items-center gap-2 flex-wrap">
                                    <span class="row-title text-xs font-semibold text-slate-800 transition-colors truncate">
                                        {{ item.title }}
                                    </span>
                                    <span v-if="item.tag" class="inline-block leading-none text-[10px] px-1 py-0.5 pt-1 rounded font-medium bg-slate-100 text-slate-600 border border-slate-200">
                                        {{ item.tag }}
                                    </span>
                                </div>
                                <p class="text-xs text-slate-500 line-clamp-1 mt-0.5 leading-snug">
                                    {{ item.message }}
                                </p>
                            </div>
                        </div>

                        <!-- Right: Timestamp + status dot -->
                        <div class="flex items-center gap-2 flex-shrink-0 z-10 text-right">
                            <span class="text-[11px] font-medium text-slate-400 whitespace-nowrap">
                                {{ item.time_formatted || item.created_at }}
                            </span>
                            <span v-if="!item.read_at" class="w-2 h-2 rounded-full bg-primary flex-shrink-0 shadow-xs" :title="$t('label.unread')"></span>
                        </div>
                    </div>
                </TransitionGroup>

                <!-- Empty state -->
                <div v-if="filteredEvents.length === 0" class="py-12 flex flex-col items-center justify-center text-center text-slate-400">
                    <div class="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-2 text-slate-400">
                        <i class="fa-regular fa-bell-slash text-lg"></i>
                    </div>
                    <p class="text-xs font-medium text-slate-600">{{ $t('label.no_recent_events') }}</p>
                    <span class="text-[11px] text-slate-400 mt-0.5">{{ $t('label.no_events_matching_filters') }}</span>
                </div>
            </div>
        </div>

        <!-- MODAL DE CONFIGURACIÓN DE FILTROS DE ACTIVIDAD -->
        <div v-if="showConfigModal" class="fixed inset-0 z-50 flex items-center justify-center bg-light/50 backdrop-blur margin-sidebar p-4 animate-fade-in animate-fade-out">
            <div class="bg-white rounded-xl shadow-lg max-h-[480px] w-full max-w-md flex flex-col overflow-hidden animate-scale-up">
                <!-- Modal Header (fijo arriba) -->
                <div class="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50 shrink-0">
                    <div class="flex items-center gap-2 text-heading font-semibold text-sm sm:text-base">
                        <i class="fa-solid fa-sliders text-primary"></i>
                        <span>{{ $t('label.configure_events_title') }}</span>
                    </div>
                    <button type="button" @click="showConfigModal = false" class="text-slate-400 hover:text-slate-600 transition">
                        <i class="fa-solid fa-xmark text-lg"></i>
                    </button>
                </div>

                <!-- Modal Body: Con scroll dinámico interno -->
                <div
                    @scroll="handleModalScroll"
                    class="p-4 flex-1 min-h-0 overflow-y-auto thin-scrolling flex flex-col gap-2"
                    :class="{ 'is-scrolling': isModalScrolling }"
                >
                    <p class="text-xs text-slate-500 mb-1 leading-relaxed">
                        {{ $t('label.configure_events_instructions') }}
                    </p>

                    <div v-for="cat in availableCategories" :key="cat.key"
                        @click="toggleCategory(cat.key)"
                        class="flex items-center justify-between px-2.5 py-2 rounded-lg border transition-all cursor-pointer select-none">
                        <div class="flex items-center gap-3">
                            <div class="w-6 h-6 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs">
                                <i :class="cat.icon"></i>
                            </div>
                            <div class="flex flex-col space-y-0.5">
                                <h4 class="text-xs font-semibold text-slate-800 leading-none">{{ $t(cat.labelKey) }}</h4>
                                <span class="text-[11px] text-slate-400 leading-none">{{ $t(cat.descKey) }}</span>
                            </div>
                        </div>

                        <!-- Switch visual -->
                        <div class="w-9 h-5 rounded-full transition-colors relative flex items-center p-0.5"
                            :class="typeFilters[cat.key] ? 'bg-primary' : 'bg-slate-300'">
                            <span class="w-4 h-4 rounded-full bg-white transition-transform transform shadow-sm"
                                :class="typeFilters[cat.key] ? 'translate-x-4' : 'translate-x-0'"></span>
                        </div>
                    </div>
                </div>

                <!-- Modal Footer (fijo abajo) -->
                <div class="px-4 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50 shrink-0">
                    <button type="button" @click="resetToDefaultFilters" class="text-xs font-medium text-slate-500 hover:text-slate-700">
                        {{ $t('label.reset_all') }}
                    </button>
                    <button type="button" @click="saveFiltersAndClose" class="px-4 py-2 rounded-lg text-xs font-semibold bg-primary text-white hover:opacity-90 transition shadow-sm">
                        {{ $t('button.save') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";

const DEFAULT_FILTERS = {
    orders: true,
    shipments: true,
    stock: true,
    network: true,
    users: true,
    coupons: true,
    flash_sales: true,
    promotions: true,
    subscribers: true,
    auth: true,
    catalog: true
};

export default {
    name: "LiveActivityComponent",
    data() {
        return {
            events: [],
            pendingQueue: [],
            pollTimer: null,
            queueTimer: null,
            isFetching: false,
            isProcessingQueue: false,
            isInitialStaggering: false,
            isFeedScrolling: false,
            isModalScrolling: false,
            feedScrollTimeout: null,
            modalScrollTimeout: null,
            filterOnlyUnread: false,
            seenIds: new Set(),
            showConfigModal: false,
            typeFilters: { ...DEFAULT_FILTERS },
            availableCategories: [
                { key: 'orders', labelKey: 'label.cat_orders_label', descKey: 'label.cat_orders_desc', icon: 'fa-solid fa-bag-shopping' },
                { key: 'shipments', labelKey: 'label.cat_shipments_label', descKey: 'label.cat_shipments_desc', icon: 'fa-solid fa-truck-fast' },
                { key: 'stock', labelKey: 'label.cat_stock_label', descKey: 'label.cat_stock_desc', icon: 'fa-solid fa-boxes-stacked' },
                { key: 'network', labelKey: 'label.cat_network_label', descKey: 'label.cat_network_desc', icon: 'fa-solid fa-wifi' },
                { key: 'users', labelKey: 'label.cat_users_label', descKey: 'label.cat_users_desc', icon: 'fa-solid fa-user-plus' },
                { key: 'coupons', labelKey: 'label.cat_coupons_label', descKey: 'label.cat_coupons_desc', icon: 'fa-solid fa-ticket' },
                { key: 'flash_sales', labelKey: 'label.cat_flash_sales_label', descKey: 'label.cat_flash_sales_desc', icon: 'fa-solid fa-bolt' },
                { key: 'promotions', labelKey: 'label.cat_promotions_label', descKey: 'label.cat_promotions_desc', icon: 'fa-solid fa-tags' },
                { key: 'subscribers', labelKey: 'label.cat_subscribers_label', descKey: 'label.cat_subscribers_desc', icon: 'fa-solid fa-envelope-open-text' },
                { key: 'auth', labelKey: 'label.cat_auth_label', descKey: 'label.cat_auth_desc', icon: 'fa-solid fa-shield-halved' },
                { key: 'catalog', labelKey: 'label.cat_catalog_label', descKey: 'label.cat_catalog_desc', icon: 'fa-solid fa-layer-group' },
            ]
        };
    },
    computed: {
        filteredEvents() {
            let list = this.events;

            // Filtrar según configuración de categorías activas
            list = list.filter(item => {
                const eventType = item.event_type || item.type || 'orders';
                return this.typeFilters[eventType] !== false;
            });

            // Filtro rápido de sólo no leídos
            if (this.filterOnlyUnread) {
                list = list.filter(e => !e.read_at);
            }

            return list;
        }
    },
    mounted() {
        this.loadFilters();
        this.fetchEvents(true);
        // Polling cada 15 segundos para consultar eventos en vivo
        this.pollTimer = setInterval(() => {
            this.fetchEvents(false);
        }, 15000);
    },
    beforeUnmount() {
        if (this.pollTimer) clearInterval(this.pollTimer);
        if (this.queueTimer) clearTimeout(this.queueTimer);
        if (this.feedScrollTimeout) clearTimeout(this.feedScrollTimeout);
        if (this.modalScrollTimeout) clearTimeout(this.modalScrollTimeout);
        this.pollTimer = null;
        this.queueTimer = null;
        this.feedScrollTimeout = null;
        this.modalScrollTimeout = null;
    },
    methods: {
        handleContainerScroll() {
            this.isFeedScrolling = true;
            if (this.feedScrollTimeout) clearTimeout(this.feedScrollTimeout);
            this.feedScrollTimeout = setTimeout(() => {
                this.isFeedScrolling = false;
            }, 800);
        },
        handleModalScroll() {
            this.isModalScrolling = true;
            if (this.modalScrollTimeout) clearTimeout(this.modalScrollTimeout);
            this.modalScrollTimeout = setTimeout(() => {
                this.isModalScrolling = false;
            }, 800);
        },
        toggleConfigModal() {
            this.showConfigModal = !this.showConfigModal;
        },
        toggleCategory(key) {
            this.typeFilters[key] = !this.typeFilters[key];
        },
        resetToDefaultFilters() {
            this.typeFilters = { ...DEFAULT_FILTERS };
            this.saveFilters();
        },
        saveFiltersAndClose() {
            this.saveFilters();
            this.showConfigModal = false;
        },
        saveFilters() {
            try {
                localStorage.setItem('dashboard_live_activity_filters', JSON.stringify(this.typeFilters));
            } catch (e) {}
        },
        loadFilters() {
            try {
                const saved = localStorage.getItem('dashboard_live_activity_filters');
                if (saved) {
                    this.typeFilters = { ...DEFAULT_FILTERS, ...JSON.parse(saved) };
                }
            } catch (e) {}
        },
        refreshManual() {
            this.fetchEvents(true);
        },
        formatTime(dateStr) {
            if (!dateStr) return "";
            try {
                const date = new Date(dateStr);
                if (isNaN(date.getTime())) return dateStr;
                const hours = date.getHours().toString().padStart(2, '0');
                const minutes = date.getMinutes().toString().padStart(2, '0');
                return `${hours}:${minutes}`;
            } catch (e) {
                return dateStr;
            }
        },
        normalizeItem(raw) {
            let time_formatted = "";
            if (raw.timestamp) {
                time_formatted = this.formatTime(raw.timestamp);
            } else {
                time_formatted = this.formatTime(new Date());
            }

            return {
                id: raw.id,
                event_type: raw.event_type || raw.type || 'orders',
                type: raw.type || raw.event_type || 'orders',
                action: raw.action || '',
                title: raw.title || this.$t('label.system_event'),
                message: raw.message || raw.description || '',
                url: raw.url || '',
                icon: raw.icon || 'fa-solid fa-circle-info',
                tag: raw.tag || '',
                severity: raw.severity || 'info',
                read_at: raw.read_at || null,
                created_at: raw.created_at || this.$t('label.recent'),
                timestamp: raw.timestamp || new Date().toISOString(),
                time_formatted: raw.time_formatted || time_formatted,
            };
        },
        fetchEvents(isInitial = false) {
            if (this.isFetching) return;
            this.isFetching = true;

            // Consultar /admin/activity-logs (con fallback a internal-notifications)
            axios.get('/admin/activity-logs', { params: { limit: 25 } })
                .then((res) => {
                    this.isFetching = false;
                    if (res.data && res.data.status) {
                        const incoming = (res.data.data || res.data.notifications || []).map(this.normalizeItem);

                        if (isInitial) {
                            const initialBatch = incoming.slice(0, 15);
                            this.events = [];
                            this.seenIds.clear();
                            this.pendingQueue = [];
                            if (this.queueTimer) clearTimeout(this.queueTimer);

                            // Para que al hacer unshift() el evento más reciente quede arriba,
                            // encolamos desde el más antiguo al más reciente del lote inicial
                            const reverseBatch = [...initialBatch].reverse();
                            reverseBatch.forEach(e => {
                                this.seenIds.add(e.id);
                                this.pendingQueue.push(e);
                            });

                            if (this.pendingQueue.length > 0) {
                                this.isInitialStaggering = true;
                                this.processQueue(true);
                            }
                        } else {
                            // En el refresh automático sólo animamos los items que no hayamos visto antes
                            const newEvents = incoming.filter(e => !this.seenIds.has(e.id));
                            if (newEvents.length > 0) {
                                // Encolamos de antiguo a nuevo para que caigan secuencialmente y queden en orden
                                const reverseNew = [...newEvents].reverse();
                                reverseNew.forEach(e => {
                                    this.seenIds.add(e.id);
                                    this.pendingQueue.push(e);
                                });
                                this.processQueue(false);
                            } else {
                                // Si no hay nuevos, únicamente sincronizamos estado de lectura sin alterar la lista ni reanimar
                                incoming.forEach(inc => {
                                    const existing = this.events.find(e => e.id === inc.id);
                                    if (existing && existing.read_at !== inc.read_at) {
                                        existing.read_at = inc.read_at;
                                    }
                                });
                            }
                        }
                    }
                })
                .catch(() => {
                    this.isFetching = false;
                });
        },
        processQueue(isStagger = false) {
            if (this.pendingQueue.length === 0) {
                this.isProcessingQueue = false;
                this.isInitialStaggering = false;
                return;
            }

            this.isProcessingQueue = true;
            if (isStagger) {
                this.isInitialStaggering = true;
            }

            const nextItem = this.pendingQueue.shift();
            this.events.unshift(nextItem);

            if (this.pendingQueue.length > 0) {
                // Intervalo de al menos 1 segundo (1000ms) entre cada item para que aparezcan uno a uno sutilmente
                this.queueTimer = setTimeout(() => {
                    this.processQueue(this.isInitialStaggering);
                }, 1000);
            } else {
                this.isProcessingQueue = false;
                this.isInitialStaggering = false;
            }
        },
        getSeverityBorder(item) {
            if (!item.read_at) {
                return 'border-l-4 border-l-primary';
            }
            if (item.severity === 'danger') return 'border-l-4 border-l-rose-500';
            if (item.severity === 'warning') return 'border-l-4 border-l-amber-500';
            if (item.severity === 'success') return 'border-l-4 border-l-emerald-500';
            return 'border-l-4 border-l-slate-300';
        },
        getBadgeStyles(item) {
            const evType = (item.event_type || item.type || '').toLowerCase();
            const sev = item.severity || 'info';

            if (sev === 'danger' || evType.includes('danger') || evType.includes('expired')) {
                return 'bg-red-100 text-red-500 border border-red-500';
            }
            if (sev === 'warning' || evType.includes('stock') || evType.includes('alert')) {
                return 'bg-amber-100 text-amber-600 border border-amber-500';
            }
            if (sev === 'success' || evType.includes('order')) {
                return 'bg-green-100 text-green-600 border border-green-500';
            }
            if (evType === 'shipments') {
                return 'bg-blue-100 text-blue-500 border border-blue-500';
            }
            if (evType === 'network') {
                return 'bg-purple-100 text-purple-600 border border-purple-500';
            }
            if (evType === 'coupons') {
                return 'bg-rose-100 text-rose-600 border border-rose-500';
            }
            if (evType === 'flash_sales') {
                return 'bg-yellow-100 text-yellow-600 border border-yellow-400';
            }
            if (evType === 'subscribers') {
                return 'bg-cyan-100 text-cyan-600 border border-cyan-400';
            }
            return 'bg-slate-100 text-slate-700 border border-slate-300';
        },
        handleEventClick(item) {
            if (!item.read_at) {
                item.read_at = new Date().toISOString();
                axios.post(`/admin/internal-notifications/${item.id}/read`).catch(() => {});
            }
        }
    }
};
</script>

<style scoped>
.stagger-slide-enter-active {
    transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}
.stagger-slide-leave-active {
    transition: all 0.3s ease-in;
    position: absolute;
    width: 100%;
}
.stagger-slide-enter-from {
    opacity: 0;
    transform: translateY(-16px) scale(0.98);
}
.stagger-slide-leave-to {
    opacity: 0;
    transform: translateY(12px);
}
.stagger-slide-move {
    transition: transform 0.5s ease;
}

.thin-scrolling {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    transition: scrollbar-color 0.3s ease;
}
.thin-scrolling::-webkit-scrollbar {
    width: 4px;
}
.thin-scrolling::-webkit-scrollbar-track {
    background: transparent;
}
.thin-scrolling::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 4px;
    transition: background-color 0.3s ease;
}

/* Mostrar la barra sólo al hacer scroll (o al posar el cursor sobre el contenedor) */
.thin-scrolling.is-scrolling,
.thin-scrolling:hover {
    scrollbar-color: #cbd5e1 transparent;
}
.thin-scrolling.is-scrolling::-webkit-scrollbar-thumb,
.thin-scrolling:hover::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
}
.thin-scrolling::-webkit-scrollbar-thumb:hover {
    background-color: #94a3b8;
}

.activity-row:hover {
    background-color: rgba(248, 250, 252, 0.95);
    border-color: #cbd5e1;
}
.activity-row:hover .row-avatar {
    transform: scale(1.05);
}
.activity-row:hover .row-title {
    color: rgb(var(--primary));
}
</style>
